import React, { useState, useEffect } from 'react';
import {
  FaTimes, FaGraduationCap, FaCalendarAlt, 
  FaClock, FaUser, FaSpinner,
  FaCheckCircle, FaExclamationCircle,
} from 'react-icons/fa';
import { createSession, updateSession } from './RecueilApi';

const INIT = {
  titre: '', dates: '', date_debut: '', date_fin: '',
  lieu: '', duree: '', consultant_nom: '', consultant_email: '',
  participants_attendus: '', lien_expire_at: '',
};

const CreateSessionModal = ({ session, onClose, onSuccess }) => {
  const isEdit = !!session;
  const [form,     setForm]     = useState(INIT);
  const [errors,   setErrors]   = useState({});
  const [loading,  setLoading]  = useState(false);
  const [globalErr,setGlobalErr]= useState(null);

  // Pré-remplir si édition
  useEffect(() => {
    if (session) {
      setForm({
        titre:                  session.titre || '',
        dates:                  session.dates || '',
        date_debut:             session.date_debut || '',
        date_fin:               session.date_fin || '',
        lieu:                   session.lieu || '',
        duree:                  session.duree || '',
        consultant_nom:         session.consultant_nom || '',
        consultant_email:       session.consultant_email || '',
        participants_attendus:  session.participants_attendus?.toString() || '',
        lien_expire_at:         session.lien_expire_at ? session.lien_expire_at.split('T')[0] : '',
      });
    }
  }, [session]);

  const set = (k, v) => {
    setForm(p => ({ ...p, [k]: v }));
    if (errors[k]) setErrors(p => { const n={...p}; delete n[k]; return n; });
  };

  const validate = () => {
    const e = {};
    if (!form.titre.trim())                            e.titre                 = 'Requis';
    if (!form.dates.trim())                            e.dates                 = 'Requis (ex: 15 - 19 Déc 2025)';
    if (!form.date_debut)                              e.date_debut            = 'Requis';
    if (!form.date_fin)                                e.date_fin              = 'Requis';
    if (!form.lieu.trim())                             e.lieu                  = 'Requis';
    if (!form.participants_attendus || parseInt(form.participants_attendus) < 1)
                                                       e.participants_attendus = 'Requis (min 1)';
    if (form.consultant_email && !/\S+@\S+\.\S+/.test(form.consultant_email))
                                                       e.consultant_email      = 'Email invalide';
    setErrors(e);
    return !Object.keys(e).length;
  };

  const submit = async () => {
    if (!validate()) return;
    setLoading(true); setGlobalErr(null);
    try {
      const payload = {
        ...form,
        participants_attendus: parseInt(form.participants_attendus),
        lien_expire_at: form.lien_expire_at || null,
      };
      const result = isEdit
        ? await updateSession(session.id, payload)
        : await createSession(payload);
      if (onSuccess) onSuccess(result);
    } catch (err) {
      if (err.errors) {
        setErrors(err.errors);
        setGlobalErr('Veuillez corriger les erreurs ci-dessous.');
      } else {
        setGlobalErr(err.message || 'Une erreur est survenue.');
      }
    } finally { setLoading(false); }
  };

  const inp = (k) => ({
    width:'100%', height:46, padding:'0 14px',
    border:`2px solid ${errors[k]?'#dc3545':'#e2e8f0'}`,
    borderRadius:10, fontSize:14, fontFamily:'inherit',
    color:'#1a202c', outline:'none', background:'#fff',
    transition:'border-color .2s',
  });

  const Field = ({ label, required, error, children, full=false }) => (
    <div style={{ gridColumn:full?'1/-1':'span 1', display:'flex', flexDirection:'column', gap:6 }}>
      <label style={{ fontSize:13, fontWeight:700, color:'#374151' }}>
        {label} {required && <span style={{ color:'#fd7e14' }}>*</span>}
      </label>
      {children}
      {error && <span style={{ fontSize:12, color:'#dc3545', fontWeight:600 }}>{error}</span>}
    </div>
  );

  const Section = ({ icon: Icon, color, title }) => (
    <div style={{ gridColumn:'1/-1', display:'flex', alignItems:'center', gap:10, margin:'8px 0 4px' }}>
      <div style={{ flex:1, height:1, background:'#f0f4f8' }}/>
      <span style={{ fontSize:11, fontWeight:800, textTransform:'uppercase', letterSpacing:1, color:'#a0aec0', display:'flex', alignItems:'center', gap:7 }}>
        <Icon style={{ color, fontSize:10 }}/>{title}
      </span>
      <div style={{ flex:1, height:1, background:'#f0f4f8' }}/>
    </div>
  );

  return (
    <>
      <style>{`
        .cmo{position:fixed;inset:0;background:rgba(15,23,42,.65);backdrop-filter:blur(6px);display:flex;align-items:center;justify-content:center;z-index:3000;padding:20px;animation:cmof .2s ease}
        @keyframes cmof{from{opacity:0}to{opacity:1}}
        .cmm{background:#fff;border-radius:22px;width:100%;max-width:680px;max-height:94vh;display:flex;flex-direction:column;box-shadow:0 24px 64px rgba(0,0,0,.22);overflow:hidden;animation:cmop .26s cubic-bezier(.34,1.56,.64,1)}
        @keyframes cmop{from{opacity:0;transform:scale(.92) translateY(16px)}to{opacity:1;transform:none}}
        .cm-inp:focus{border-color:#fd7e14!important;box-shadow:0 0 0 4px rgba(253,126,20,.1)}
        .cmspin{animation:cmspinn .8s linear infinite}
        @keyframes cmspinn{to{transform:rotate(360deg)}}
      `}</style>

      <div className="cmo" onClick={onClose}>
        <div className="cmm" onClick={e => e.stopPropagation()}>

          {/* HEADER */}
          <div style={{ padding:'20px 24px', borderBottom:'2px solid #f0f4f8', display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
            <div style={{ display:'flex', alignItems:'center', gap:13 }}>
              <div style={{ width:46, height:46, borderRadius:13, background:'rgba(253,126,20,.1)', color:'#fd7e14', display:'flex', alignItems:'center', justifyContent:'center', fontSize:20, flexShrink:0 }}>
                <FaGraduationCap />
              </div>
              <div>
                <div style={{ fontSize:18, fontWeight:900, color:'#1a202c', marginBottom:2 }}>
                  {isEdit ? 'Modifier la session' : 'Nouvelle session de recueil'}
                </div>
                <div style={{ fontSize:13, color:'#718096' }}>
                  {isEdit ? 'Mettez à jour les informations de la session' : 'Créez une session pour collecter les attentes participants'}
                </div>
              </div>
            </div>
            <button onClick={onClose} style={{ width:36, height:36, borderRadius:9, border:'2px solid #e2e8f0', background:'transparent', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', color:'#718096', flexShrink:0 }}>
              <FaTimes />
            </button>
          </div>

          {/* BODY */}
          <div style={{ overflowY:'auto', flex:1, padding:'24px' }}>
            {globalErr && (
              <div style={{ display:'flex', alignItems:'center', gap:10, padding:'12px 16px', background:'rgba(220,53,69,.06)', border:'1px solid rgba(220,53,69,.2)', borderRadius:11, color:'#dc3545', fontSize:14, fontWeight:600, marginBottom:20 }}>
                <FaExclamationCircle style={{ flexShrink:0 }}/> {globalErr}
              </div>
            )}

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:18 }}>

              {/* Section Formation */}
              <Section icon={FaGraduationCap} color="#fd7e14" title="Informations de la formation" />

              <Field label="Intitulé de la formation" required error={errors.titre} full>
                <input className="cm-inp" value={form.titre} onChange={e=>set('titre',e.target.value)}
                  placeholder="ex : Management d'équipe" style={inp('titre')}/>
              </Field>

              <Field label="Dates affichées" required error={errors.dates}
                full={false}>
                <input className="cm-inp" value={form.dates} onChange={e=>set('dates',e.target.value)}
                  placeholder="ex : 15 - 19 Déc 2025" style={inp('dates')}/>
              </Field>

              <Field label="Lieu" required error={errors.lieu}>
                <input className="cm-inp" value={form.lieu} onChange={e=>set('lieu',e.target.value)}
                  placeholder="ex : Cotonou, CINEF" style={inp('lieu')}/>
              </Field>

              {/* Section Dates */}
              <Section icon={FaCalendarAlt} color="#0d6efd" title="Dates précises" />

              <Field label="Date de début" required error={errors.date_debut}>
                <input type="date" className="cm-inp" value={form.date_debut} onChange={e=>set('date_debut',e.target.value)} style={inp('date_debut')}/>
              </Field>

              <Field label="Date de fin" required error={errors.date_fin}>
                <input type="date" className="cm-inp" value={form.date_fin} onChange={e=>set('date_fin',e.target.value)} style={inp('date_fin')}/>
              </Field>

              {/* Section Détails */}
              <Section icon={FaClock} color="#8b5cf6" title="Détails" />

              <Field label="Durée" error={errors.duree}>
                <input className="cm-inp" value={form.duree} onChange={e=>set('duree',e.target.value)}
                  placeholder="ex : 5 jours · 35h" style={inp('duree')}/>
              </Field>

              <Field label="Nombre de participants attendus" required error={errors.participants_attendus}>
                <input type="number" min="1" className="cm-inp" value={form.participants_attendus}
                  onChange={e=>set('participants_attendus',e.target.value)}
                  placeholder="ex : 15" style={inp('participants_attendus')}/>
              </Field>

              <Field label="Date d'expiration des liens" error={errors.lien_expire_at} full>
                <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                  <input type="date" className="cm-inp" value={form.lien_expire_at}
                    onChange={e=>set('lien_expire_at',e.target.value)} style={{ ...inp('lien_expire_at'), width:240 }}/>
                  <span style={{ fontSize:12, color:'#a0aec0' }}>Laissez vide → 30 jours par défaut à l'envoi</span>
                </div>
              </Field>

              {/* Section Consultant */}
              <Section icon={FaUser} color="#198754" title="Formateur / Consultant" />

              <Field label="Nom du consultant" error={errors.consultant_nom}>
                <input className="cm-inp" value={form.consultant_nom} onChange={e=>set('consultant_nom',e.target.value)}
                  placeholder="Nom du formateur" style={inp('consultant_nom')}/>
              </Field>

              <Field label="Email du consultant" error={errors.consultant_email}>
                <input type="email" className="cm-inp" value={form.consultant_email}
                  onChange={e=>set('consultant_email',e.target.value)}
                  placeholder="formateur@cinef.com" style={inp('consultant_email')}/>
              </Field>

            </div>
          </div>

          {/* FOOTER */}
          <div style={{ padding:'16px 24px', borderTop:'2px solid #f0f4f8', display:'flex', justifyContent:'space-between', alignItems:'center', background:'#fafbfc', flexShrink:0 }}>
            <div style={{ fontSize:13, color:'#a0aec0' }}>
              {isEdit ? `Session : ${session.titre}` : 'Tous les champs * sont obligatoires'}
            </div>
            <div style={{ display:'flex', gap:10 }}>
              <button onClick={onClose} style={{ padding:'11px 22px', border:'2px solid #e2e8f0', borderRadius:10, background:'transparent', cursor:'pointer', fontSize:14, fontWeight:700, color:'#718096', fontFamily:'inherit' }}>
                Annuler
              </button>
              <button onClick={submit} disabled={loading} style={{
                display:'flex', alignItems:'center', gap:9, padding:'11px 26px',
                border:'none', borderRadius:10,
                background: loading?'#a0aec0':'linear-gradient(135deg,#fd7e14,#e8700a)',
                color:'#fff', cursor:loading?'not-allowed':'pointer',
                fontSize:14, fontWeight:800, fontFamily:'inherit',
                boxShadow:loading?'none':'0 6px 20px rgba(253,126,20,.3)',
              }}>
                {loading
                  ? <><FaSpinner className="cmspin" style={{ fontSize:13 }}/> Enregistrement...</>
                  : <><FaCheckCircle style={{ fontSize:12 }}/> {isEdit?'Enregistrer':'Créer la session'}</>
                }
              </button>
            </div>
          </div>

        </div>
      </div>
    </>
  );
};

export default CreateSessionModal;